import React, { useState, useEffect, useMemo } from 'react';
import { 
  Send, CheckCircle, Eye, Link2, Download, Mail, MessageSquare, Shield, Clock, Target,
  AlertCircle, TrendingUp, TrendingDown, Activity, Inbox, Calendar, XCircle, 
  RefreshCw, Filter, BarChart3, Zap, Users, FileText
} from 'lucide-react';
import StatCard from '../components/StatCard';
import Papa from 'papaparse';
import api from '../services/api';

const ReportsPage = ({ campaigns, deliverabilityScore }) => {
  const [activeTab, setActiveTab] = useState('emails');
  const [emailAnalytics, setEmailAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [dateRange, setDateRange] = useState('7d'); // 7d, 30d, all

  // Load email analytics
  useEffect(() => {
    if (activeTab === 'emails') {
      loadEmailAnalytics();
    }
  }, [activeTab, dateRange]);

  const loadEmailAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const data = await api.analytics.getEmailAnalytics();
      setEmailAnalytics(data);
    } catch (error) {
      console.error('Failed to load email analytics', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Security Tab Content (existing reports)
  const SecurityTab = () => {
  const totalSent = campaigns.reduce((acc, c) => acc + c.sent, 0);
  const totalDelivered = campaigns.reduce((acc, c) => acc + c.delivered, 0);
  const totalOpened = campaigns.reduce((acc, c) => acc + c.opened, 0);
  const totalClicked = campaigns.reduce((acc, c) => acc + c.clicked, 0);
  const avgOpenRate = totalDelivered > 0 ? ((totalOpened / totalDelivered) * 100).toFixed(1) : 0;
  const avgClickRate = totalOpened > 0 ? ((totalClicked / totalOpened) * 100).toFixed(1) : 0;
  const hasDeliverabilityScore = typeof deliverabilityScore === 'number';
  const deliverabilityLabel = hasDeliverabilityScore
    ? (deliverabilityScore > 80 ? 'Excellent' : 'Needs improvement')
    : 'Not enough data';
  const deliverabilityValue = hasDeliverabilityScore ? `${deliverabilityScore}/100` : '—';

  const exportAllCampaigns = () => {
    const allData = campaigns.map(c => ({
      'Campaign': c.name,
      'Channel': c.channel,
      'Sent': c.sent,
      'Delivered': c.delivered,
      'Delivery Rate': ((c.delivered / c.sent) * 100).toFixed(2) + '%',
      'Opened': c.opened,
      'Open Rate': c.channel === 'email' ? ((c.opened / c.delivered) * 100).toFixed(2) + '%' : 'N/A',
      'Clicked': c.clicked,
      'Click Rate': c.channel === 'email' ? ((c.clicked / c.opened) * 100).toFixed(2) + '%' : 'N/A',
      'Date': new Date(c.createdAt).toLocaleDateString()
    }));
    const csv = Papa.unparse(allData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all-campaigns-report-${Date.now()}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          label="Total Sent" 
          value={totalSent} 
          icon={Send} 
          color="from-blue-500 to-cyan-500" 
          change="All time"
          trend="up" 
        />
        <StatCard 
          label="Total Delivered" 
          value={totalDelivered} 
          icon={CheckCircle} 
          color="from-green-500 to-emerald-500" 
          change={`${totalSent > 0 ? ((totalDelivered/totalSent)*100).toFixed(1) : 0}% rate`}
          trend="up" 
        />
        <StatCard 
          label="Avg Open Rate" 
          value={`${avgOpenRate}%`} 
          icon={Eye} 
          color="from-purple-500 to-pink-500" 
          change="Email campaigns"
          trend="up" 
        />
        <StatCard 
          label="Avg Click Rate" 
          value={`${avgClickRate}%`} 
          icon={Link2} 
          color="from-orange-500 to-red-500" 
          change="Email engagement"
          trend="up" 
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Campaign Performance */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Campaign Performance</h2>
          <div className="space-y-4">
            {campaigns.map(campaign => {
              const deliveryRateValue = campaign.sent > 0
                ? (campaign.delivered / campaign.sent) * 100
                : null;
              const openRateValue = campaign.channel === 'email' && campaign.delivered > 0
                ? (campaign.opened / campaign.delivered) * 100
                : null;
              const clickRateValue = campaign.channel === 'email' && campaign.opened > 0
                ? (campaign.clicked / campaign.opened) * 100
                : null;

              const deliveryRate = deliveryRateValue !== null ? deliveryRateValue.toFixed(1) : '—';
              const openRate = openRateValue !== null ? openRateValue.toFixed(1) : '—';
              const clickRate = clickRateValue !== null ? clickRateValue.toFixed(1) : '—';
              
              return (
                <div key={campaign.id} className="p-4 bg-gradient-to-r from-gray-50 to-indigo-50 rounded-xl hover:shadow-md transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 ${campaign.channel === 'email' ? 'bg-indigo-100' : 'bg-purple-100'} rounded-lg`}>
                        {campaign.channel === 'email' 
                          ? <Mail className="text-indigo-600" size={16} /> 
                          : <MessageSquare className="text-purple-600" size={16} />
                        }
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{campaign.name}</div>
                        <div className="text-xs text-gray-600">{new Date(campaign.createdAt).toLocaleDateString()}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const csv = Papa.unparse([{
                          'Campaign': campaign.name,
                          'Channel': campaign.channel,
                          'Sent': campaign.sent,
                          'Delivered': campaign.delivered,
                          'Delivery Rate': deliveryRateValue !== null ? `${deliveryRateValue.toFixed(2)}%` : 'N/A',
                          'Opened': campaign.opened,
                          'Open Rate': openRateValue !== null ? `${openRateValue.toFixed(2)}%` : 'N/A',
                          'Clicked': campaign.clicked,
                          'Click Rate': clickRateValue !== null ? `${clickRateValue.toFixed(2)}%` : 'N/A',
                          'Created At': new Date(campaign.createdAt).toISOString()
                        }]);
                        const blob = new Blob([csv], { type: 'text/csv' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `campaign-${campaign.name}-${Date.now()}.csv`;
                        a.click();
                      }}
                      className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-lg transition-colors duration-200"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-600">Delivery Rate</span>
                        <span className="font-semibold text-green-600">
                          {deliveryRateValue !== null ? `${deliveryRate}%` : '—'}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: deliveryRateValue !== null ? `${Math.min(deliveryRateValue, 100)}%` : '0%' }}
                        ></div>
                      </div>
                    </div>
                    {campaign.channel === 'email' && (
                      <>
                        <div>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-gray-600">Open Rate</span>
                            <span className="font-semibold text-indigo-600">
                              {openRateValue !== null ? `${openRate}%` : '—'}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                              style={{ width: openRateValue !== null ? `${Math.min(openRateValue, 100)}%` : '0%' }}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-gray-600">Click Rate</span>
                            <span className="font-semibold text-purple-600">
                              {clickRateValue !== null ? `${clickRate}%` : '—'}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                              style={{ width: clickRateValue !== null ? `${Math.min(clickRateValue, 100)}%` : '0%' }}
                            ></div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            {campaigns.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Send className="mx-auto mb-3 text-gray-400" size={48} />
                <p>No campaign data available</p>
              </div>
            )}
          </div>
        </div>

        {/* Engagement Insights */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Engagement Insights</h2>
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Target className="text-indigo-600" size={20} />
                  </div>
                  <div className="font-semibold text-gray-900">Best Performing</div>
                </div>
                {campaigns.length > 0 ? (
                  <>
                    <div className="text-xl font-bold text-indigo-600">
                      {campaigns.reduce((best, c) => {
                        const rate = c.delivered > 0 ? (c.opened / c.delivered) : 0;
                        const bestRate = best.delivered > 0 ? (best.opened / best.delivered) : 0;
                        return rate > bestRate ? c : best;
                      }).name}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {(campaigns.reduce((best, c) => {
                        const rate = c.delivered > 0 ? (c.opened / c.delivered) : 0;
                        const bestRate = best.delivered > 0 ? (best.opened / best.delivered) : 0;
                        return rate > bestRate ? c : best;
                      }).opened / campaigns.reduce((best, c) => {
                        const rate = c.delivered > 0 ? (c.opened / c.delivered) : 0;
                        const bestRate = best.delivered > 0 ? (best.opened / best.delivered) : 0;
                        return rate > bestRate ? c : best;
                      }).delivered * 100).toFixed(1)}% open rate
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-gray-500">No data yet</div>
                )}
              </div>

              <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Clock className="text-green-600" size={20} />
                  </div>
                  <div className="font-semibold text-gray-900">Optimal Send Time</div>
                </div>
                <div className="text-xl font-bold text-green-600">10:00 AM - 2:00 PM</div>
                <div className="text-sm text-gray-600 mt-1">Peak engagement window</div>
              </div>

              <div className="p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Shield className="text-orange-600" size={20} />
                  </div>
                  <div className="font-semibold text-gray-900">Health Score</div>
                </div>
                <div className="text-xl font-bold text-orange-600">{deliverabilityValue}</div>
                <div className="text-sm text-gray-600 mt-1">{deliverabilityLabel}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    );
  };

  // Emails Tab Content
  const EmailsTab = () => {
    // Filter timeline by date range - must be called before any early returns
    const filteredTimeline = useMemo(() => {
      if (!emailAnalytics || !emailAnalytics.timeline) return [];
      const timeline = emailAnalytics.timeline;
      if (dateRange === 'all') return timeline;
      const days = dateRange === '7d' ? 7 : 30;
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      return timeline.filter(item => new Date(item.date) >= cutoff);
    }, [emailAnalytics, dateRange]);

    if (loadingAnalytics) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="animate-spin text-indigo-600" size={32} />
          <span className="ml-3 text-gray-600">Loading email analytics...</span>
        </div>
      );
    }

    if (!emailAnalytics) {
      return (
        <div className="text-center py-12 text-gray-500">
          <Mail className="mx-auto mb-3 text-gray-400" size={48} />
          <p>No email analytics data available</p>
        </div>
      );
    }

    const { summary, pending, campaigns: emailCampaigns, batches, timeline } = emailAnalytics;

    return (
      <div className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard 
            label="Total Sent" 
            value={summary.totalSent.toLocaleString()} 
            icon={Send} 
            color="from-blue-500 to-cyan-500" 
            change={`${summary.deliveryRate.toFixed(1)}% delivered`}
            trend="up" 
          />
          <StatCard 
            label="Delivered" 
            value={summary.totalDelivered.toLocaleString()} 
            icon={CheckCircle} 
            color="from-green-500 to-emerald-500" 
            change={`${summary.openRate.toFixed(1)}% opened`}
            trend="up" 
          />
          <StatCard 
            label="Bounced" 
            value={summary.totalBounced.toLocaleString()} 
            icon={XCircle} 
            color="from-red-500 to-rose-500" 
            change={`${summary.bounceRate.toFixed(2)}% rate`}
            trend={summary.bounceRate > 2 ? 'down' : 'up'} 
          />
          <StatCard 
            label="Opened" 
            value={summary.totalOpened.toLocaleString()} 
            icon={Eye} 
            color="from-purple-500 to-pink-500" 
            change={`${summary.clickRate.toFixed(1)}% clicked`}
            trend="up" 
          />
        </div>

        {/* Pending & Scheduled */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Inbox className="text-amber-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Pending Emails</h3>
                <p className="text-sm text-gray-600">In queue</p>
              </div>
            </div>
            <div className="text-3xl font-bold text-amber-600">{pending.totalPendingEmails.toLocaleString()}</div>
            <div className="mt-2 text-sm text-gray-600">
              {pending.queued} queued • {pending.inProgress} in progress
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Calendar className="text-blue-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Scheduled</h3>
                <p className="text-sm text-gray-600">Future sends</p>
              </div>
            </div>
            <div className="text-3xl font-bold text-blue-600">{pending.scheduled}</div>
            <div className="mt-2 text-sm text-gray-600">
              {pending.scheduled} campaigns scheduled
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertCircle className="text-red-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Complaints</h3>
                <p className="text-sm text-gray-600">Spam reports</p>
              </div>
            </div>
            <div className="text-3xl font-bold text-red-600">{summary.totalComplained}</div>
            <div className="mt-2 text-sm text-gray-600">
              {summary.totalUnsubscribed} unsubscribed
            </div>
          </div>
        </div>

        {/* Email Activity Timeline */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <BarChart3 className="text-indigo-600" size={20} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Email Activity Timeline</h2>
                <p className="text-sm text-gray-600">Visualized sending logs</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="all">All time</option>
              </select>
              <button
                onClick={loadEmailAnalytics}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <RefreshCw size={18} />
              </button>
            </div>
          </div>
          
          {filteredTimeline.length > 0 ? (
            <div className="space-y-4">
              {filteredTimeline.map((day, idx) => {
                const maxProcessed = Math.max(...filteredTimeline.map(d => d.totalProcessed), 1);
                const widthPercent = (day.totalProcessed / maxProcessed) * 100;
                
                return (
                  <div key={day.date} className="flex items-center gap-4">
                    <div className="w-24 text-sm text-gray-600 font-medium">
                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-gray-900">
                          {day.totalProcessed.toLocaleString()} emails sent
                        </span>
                        {day.totalErrors > 0 && (
                          <span className="text-xs text-red-600 font-medium">
                            {day.totalErrors} errors
                          </span>
                        )}
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${Math.max(widthPercent, 5)}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {day.batches.length} batch{day.batches.length !== 1 ? 'es' : ''} processed
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Activity className="mx-auto mb-2 text-gray-400" size={32} />
              <p>No activity in this time period</p>
            </div>
          )}
        </div>

        {/* Recent Email Logs */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileText className="text-purple-600" size={20} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Recent Email Logs</h2>
                <p className="text-sm text-gray-600">Latest batch processing activity</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {batches.slice(0, 50).map((batch) => {
              const campaign = emailCampaigns.find(c => c.id === batch.campaignId);
              const statusColor = {
                'completed': 'bg-green-100 text-green-800',
                'failed': 'bg-red-100 text-red-800',
                'skipped': 'bg-gray-100 text-gray-800'
              }[batch.status] || 'bg-gray-100 text-gray-800';

              return (
                <div key={batch.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${statusColor}`}>
                          {batch.status}
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {campaign?.name || 'Unknown Campaign'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>Processed: {batch.processedDelta} emails (Total: {batch.processedTotal})</div>
                        <div>Duration: {(batch.durationMs / 1000).toFixed(1)}s</div>
                        <div>Time: {new Date(batch.attemptedAt).toLocaleString()}</div>
                        {batch.error && (
                          <div className="text-red-600 font-medium mt-2">
                            Error: {batch.error}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {batches.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FileText className="mx-auto mb-2 text-gray-400" size={32} />
                <p>No email logs available</p>
              </div>
            )}
          </div>
        </div>

        {/* Scheduled Deletion Emails */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Calendar className="text-orange-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Scheduled Campaigns</h2>
              <p className="text-sm text-gray-600">Emails scheduled for future delivery</p>
            </div>
          </div>
          
          <div className="space-y-3">
            {emailCampaigns
              .filter(c => c.status === 'scheduled' && c.scheduledDate)
              .map(campaign => (
                <div key={campaign.id} className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Mail className="text-indigo-600" size={18} />
                        <span className="font-semibold text-gray-900">{campaign.name}</span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>Recipients: {campaign.recipientCount?.toLocaleString() || 0}</div>
                        <div>Scheduled: {new Date(campaign.scheduledDate).toLocaleString()}</div>
                        <div>Template: {campaign.template || 'N/A'}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500 mb-1">Time until send</div>
                      <div className="text-sm font-semibold text-indigo-600">
                        {(() => {
                          const now = new Date();
                          const scheduled = new Date(campaign.scheduledDate);
                          const diff = scheduled - now;
                          if (diff < 0) return 'Overdue';
                          const hours = Math.floor(diff / (1000 * 60 * 60));
                          const days = Math.floor(hours / 24);
                          if (days > 0) return `${days}d ${hours % 24}h`;
                          return `${hours}h`;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            {emailCampaigns.filter(c => c.status === 'scheduled' && c.scheduledDate).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="mx-auto mb-2 text-gray-400" size={32} />
                <p>No scheduled campaigns</p>
              </div>
            )}
          </div>
        </div>

        {/* Campaign Performance Table */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="text-green-600" size={20} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">All Email Campaigns</h2>
                <p className="text-sm text-gray-600">Complete performance overview</p>
              </div>
            </div>
            <button
              onClick={() => {
                const csv = Papa.unparse(emailCampaigns.map(c => ({
                  'Campaign': c.name,
                  'Status': c.status,
                  'Sent': c.sent || 0,
                  'Delivered': c.delivered || 0,
                  'Bounced': c.bounced || 0,
                  'Opened': c.opened || 0,
                  'Clicked': c.clicked || 0,
                  'Unsubscribed': c.unsubscribed || 0,
                  'Complained': c.complained || 0,
                  'Created': new Date(c.createdAt).toISOString(),
                  'Scheduled': c.scheduledDate ? new Date(c.scheduledDate).toISOString() : ''
                })));
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `email-analytics-${Date.now()}.csv`;
                a.click();
              }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
            >
              <Download size={16} />
              Export CSV
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Campaign</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Sent</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Delivered</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Opened</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Clicked</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Bounced</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Created</th>
                </tr>
              </thead>
              <tbody>
                {emailCampaigns.map(campaign => (
                  <tr key={campaign.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">{campaign.name}</div>
                      <div className="text-xs text-gray-500">{campaign.template || 'N/A'}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        campaign.status === 'completed' ? 'bg-green-100 text-green-800' :
                        campaign.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        campaign.status === 'scheduled' ? 'bg-amber-100 text-amber-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {campaign.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-medium">{campaign.sent?.toLocaleString() || 0}</td>
                    <td className="py-3 px-4 text-right text-green-600 font-medium">{campaign.delivered?.toLocaleString() || 0}</td>
                    <td className="py-3 px-4 text-right text-indigo-600 font-medium">{campaign.opened?.toLocaleString() || 0}</td>
                    <td className="py-3 px-4 text-right text-purple-600 font-medium">{campaign.clicked?.toLocaleString() || 0}</td>
                    <td className="py-3 px-4 text-right text-red-600 font-medium">{campaign.bounced?.toLocaleString() || 0}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(campaign.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {emailCampaigns.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Mail className="mx-auto mb-3 text-gray-400" size={48} />
                <p>No email campaigns found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-600 mt-1">Track performance and engagement metrics across all campaigns</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl p-2 shadow-lg border border-gray-100 inline-flex gap-2">
        <button
          onClick={() => setActiveTab('emails')}
          className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 ${
            activeTab === 'emails'
              ? 'bg-indigo-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Mail size={18} />
          Emails
        </button>
        <button
          onClick={() => setActiveTab('security')}
          className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 ${
            activeTab === 'security'
              ? 'bg-indigo-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Shield size={18} />
          Security
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'emails' ? <EmailsTab /> : <SecurityTab />}
    </div>
  );
};

export default ReportsPage;
